import React, { useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { css } from '@emotion/css'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { Text } from '@contrast-security-inc/design-system-typography'
import { IconInformation } from '@contrast-security-inc/design-system-foundations'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { Radio, RadioGroup } from '@contrast-security-inc/design-system-inputs'
import { FormValidator, Box, Row } from '../../legacy-design-system/form'
import { Dialog, DialogFooter } from '../../legacy-design-system/modal'
import { Form } from './form-components/form'
import { ServerNameInput } from './form-components/server-name-input'
import { ServerEnvironmentInput } from './form-components/server-environment-input'
import { LogLevelInput } from './form-components/log-level-input'
import { LogPathInput } from './form-components/log-path-input'
import { StacktraceCaptureModeInput } from './form-components/stacktrace-capture-mode-input'
import { BotBlockingInput } from './form-components/bot-blocking-input'
import { SamplingToggleInput } from './form-components/sampling-toggle-input'
import { SamplingBaselineInput } from './form-components/sampling-baseline-input'
import { SamplingFrequencyInput } from './form-components/sampling-frequency-input'
import { SamplingWindowInput } from './form-components/sampling-window-input'
import { SyslogToggleInput } from './form-components/syslog-toggle-input'
import { SyslogSettingsForm, SyslogSettingsFormState } from '../syslog'
import { CancelButton } from './form-components/cancel-button'
import { SaveButton } from './form-components/save-button'
import { DoneButton } from './form-components/done-button'
import { ServerSettingsFormValue, ServerSettingsFormSetterFactory } from '.'
import { formatStyles } from '../react-intl-helpers'
import { SplitContext } from '../../ng/splitio-wrapper'

export const samplingProfileKey = 'samplingProfile'
export const SAMPLING_OPTIONS = {
  PROD: 'PRODUCTION',
  QA: 'QA',
  DEV: 'DEVELOPMENT',
  CUSTOM: 'CUSTOM'
}
const radioGroupId = 'radio-group-sampling'
const radioStackCss = css`
  justify-content: flex-start;
  align-items: center;
`
export const SamplingSettings = ({ formSetter, formValue, onChange }) => {
  const intl = useIntl()
  const sampleOption = formValue?.[samplingProfileKey] || SAMPLING_OPTIONS.PROD
  const showWarning =
    (sampleOption === SAMPLING_OPTIONS.QA ||
      sampleOption === SAMPLING_OPTIONS.DEV) &&
    formValue?.['environment'] === SAMPLING_OPTIONS.PROD
  const onOptionChange = React.useCallback(
    (option) => {
      // nexttick required for formSetter
      setTimeout(() => formSetter(samplingProfileKey)(option))

      if (onChange) {
        onChange(option)
      }
    },
    [onChange, formSetter]
  )
  const canApplyDarkTheme = useMemo(() => {
    return window.canApplyDarkTheme === true
  }, [])

  return (
    <div
      className={css`
        margin-bottom: 8px;
      `}
    >
      <div
        className={css`
          display: flex;
          flex-direction: column;
          margin-bottom: 16px;
        `}
      >
        <Text font="titleH3">
          {intl.formatMessage({ id: 'title_assesAnalysisFineTuning' })}
        </Text>
        <Text font="body">
          {intl.formatMessage(
            { id: 'detail_assesAnalysisFineTuning' },
            { ...formatStyles.doclink({ path: '/application-sampling.html' }) }
          )}
        </Text>
      </div>
      <RadioGroup name={radioGroupId}>
        <Stack className={radioStackCss} spacing="4px">
          <Radio
            testId="sampling-options-dev"
            value={SAMPLING_OPTIONS.DEV}
            label={intl.formatMessage({ id: 'title_fineTunedDev' })}
            onChange={onOptionChange.bind(null, SAMPLING_OPTIONS.DEV)}
            isChecked={sampleOption === SAMPLING_OPTIONS.DEV}
          />
          <Text
            font="metadata"
            color={canApplyDarkTheme ? 'neutral40' : 'neutral70'}
          >
            {intl.formatMessage({ id: 'detail_fineTunedDev' })}
          </Text>
        </Stack>
        <Stack className={radioStackCss}>
          <Radio
            testId="sampling-options-qa"
            value={SAMPLING_OPTIONS.QA}
            label={intl.formatMessage({ id: 'title_fineTunedQA' })}
            onChange={onOptionChange.bind(null, SAMPLING_OPTIONS.QA)}
            isChecked={sampleOption === SAMPLING_OPTIONS.QA}
          />
          <Text
            font="metadata"
            color={canApplyDarkTheme ? 'neutral40' : 'neutral70'}
          >
            {intl.formatMessage({ id: 'detail_fineTunedQA' })}
          </Text>
        </Stack>
        <Stack className={radioStackCss}>
          <Radio
            testId="sampling-options-prod"
            value={SAMPLING_OPTIONS.PROD}
            label={intl.formatMessage({ id: 'title_fineTunedProd' })}
            onChange={onOptionChange.bind(null, SAMPLING_OPTIONS.PROD)}
            isChecked={sampleOption === SAMPLING_OPTIONS.PROD}
          />
          <Text
            font="metadata"
            color={canApplyDarkTheme ? 'neutral40' : 'neutral70'}
          >
            {intl.formatMessage({ id: 'detail_fineTunedProd' })}
          </Text>
        </Stack>
        <Stack className={radioStackCss}>
          <Radio
            testId="sampling-options-custom"
            value={SAMPLING_OPTIONS.CUSTOM}
            label={intl.formatMessage({ id: 'detail_customOption' })}
            onChange={onOptionChange.bind(null, SAMPLING_OPTIONS.CUSTOM)}
            isChecked={sampleOption === SAMPLING_OPTIONS.CUSTOM}
          />
          <Tooltip
            body={intl.formatMessage({
              id: 'tooltip_customSamplingAndStacktraces'
            })}
          >
            <IconInformation size="sm" />
          </Tooltip>
        </Stack>
      </RadioGroup>
      {showWarning && (
        <div
          className={css`
            padding-top: 16px;
          `}
        >
          {intl.formatMessage({ id: 'detail_serverSamplingWarning' })}
        </div>
      )}
    </div>
  )
}

const dialogSize = 'large'
const firstColumnWidth = 5
const secondColumnWidth = 3
const thirdColumnWidth = 4

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {
  // this is intentional
}

export const isSaveButtonDisabled = ({
  hasUnsavedChanges,
  formIsValid
}: {
  hasUnsavedChanges: boolean
  formIsValid: boolean
}) => {
  return !hasUnsavedChanges || (hasUnsavedChanges && !formIsValid)
}

export interface ServerSettingsFormSingleViewProps {
  isOpen: boolean
  formValue: ServerSettingsFormValue
  language: string
  isAllowedAssess: boolean
  isAllowedProtect: boolean
  isAllowedSyslog: boolean
  isAllowedStackTraces: boolean
  showLogPath: boolean
  disableBotProtectToggle: boolean
  disableSamplingToggle: boolean
  disableSyslogToggle: boolean
  showSampling: boolean
  showSyslog: boolean

  onCancel: () => void
  onConfirm: () => void
  formSetter: ServerSettingsFormSetterFactory
  syslogOnChange: (value: SyslogSettingsFormState) => void

  hasUnsavedChanges: boolean
  isReadOnly?: boolean
}

export function ServerSettingsFormSingleView({
  isOpen,
  formValue,
  language,
  isAllowedAssess,
  isAllowedProtect,
  isAllowedSyslog,
  isAllowedStackTraces,
  showLogPath,
  disableBotProtectToggle,
  disableSamplingToggle,
  disableSyslogToggle,
  showSampling,
  showSyslog,
  onCancel,
  onConfirm,
  formSetter,
  syslogOnChange,
  hasUnsavedChanges,
  isReadOnly = false
}: ServerSettingsFormSingleViewProps) {
  const [isCustomSamplingVisible, setIsCustomSamplingVisible] = React.useState(
    formValue?.[samplingProfileKey] === SAMPLING_OPTIONS.CUSTOM
  )
  const isBotProtectToggleDisabled = isReadOnly || disableBotProtectToggle
  const isSamplingToggleDisabled = isReadOnly || disableSamplingToggle
  const isSyslogToggleDisabled = isReadOnly || disableSyslogToggle
  // prettier-ignore
  const onSamplingChange = React.useCallback((option) => {
    setIsCustomSamplingVisible(option === SAMPLING_OPTIONS.CUSTOM)
  }, [])
  const isSupportedLanguage =
    language.toUpperCase() !== 'PHP' && language.toUpperCase() !== 'GO'
  const ffAssessSamplingProfiles =
    React.useContext(SplitContext).isSplitTreatmentOn(
      'PROD-2881_assess_sampling_profiles'
    ) && isSupportedLanguage

  return (
    <FormattedMessage id="title_servers_serverSettings">
      {(title) => (
        <FormValidator value={formValue}>
          {({ getFormFieldProps, formIsValid }) => {
            return (
              <Dialog
                isOpen={isOpen}
                onCancel={onCancel}
                renderTitle={() => title}
                size={dialogSize}
              >
                <Form>
                  <Row>
                    <Box basis={firstColumnWidth}>
                      <ServerNameInput
                        value={formValue.name}
                        getFormFieldProps={getFormFieldProps}
                        onChange={formSetter('name')}
                        isReadOnly={isReadOnly}
                      />
                    </Box>
                    <Box basis={secondColumnWidth}>
                      <ServerEnvironmentInput
                        value={formValue.environment}
                        edited={false}
                        onChange={formSetter('environment')}
                        isReadOnly={isReadOnly}
                      />
                    </Box>
                    <Box basis={thirdColumnWidth}>
                      <LogLevelInput
                        value={formValue.logLevel}
                        edited={false}
                        onChange={formSetter('logLevel')}
                        isReadOnly={isReadOnly}
                      />
                    </Box>
                  </Row>
                  <Row>
                    {showLogPath && (
                      <Box basis={firstColumnWidth}>
                        <LogPathInput
                          value={formValue.logPath}
                          onChange={formSetter('logPath')}
                          getFormFieldProps={getFormFieldProps}
                          isReadOnly={isReadOnly}
                        />
                      </Box>
                    )}
                    {isAllowedProtect && (
                      <Box basis="content">
                        <BotBlockingInput
                          value={formValue.botProtection}
                          disabled={isBotProtectToggleDisabled}
                          disableBotProtectToggle={isBotProtectToggleDisabled}
                          edited={false}
                          onChange={
                            isReadOnly ? noop : formSetter('botProtection')
                          }
                        />
                      </Box>
                    )}
                  </Row>
                  {!ffAssessSamplingProfiles && isAllowedAssess && (
                    <Row>
                      <Box>
                        <div
                          className={css`
                            padding: 16px 0px 2px;
                          `}
                        >
                          <SamplingToggleInput
                            disabled={isSamplingToggleDisabled}
                            disableSamplingToggle={isSamplingToggleDisabled}
                            value={formValue.samplingEnabled}
                            edited={false}
                            onChange={formSetter('samplingEnabled')}
                          />
                        </div>
                      </Box>
                    </Row>
                  )}
                  {ffAssessSamplingProfiles && (
                    <SamplingSettings
                      formValue={formValue}
                      formSetter={formSetter}
                      onChange={onSamplingChange}
                    />
                  )}
                  {((showSampling && !ffAssessSamplingProfiles) ||
                    (ffAssessSamplingProfiles && isCustomSamplingVisible)) && (
                    <>
                      {isAllowedAssess && isAllowedStackTraces && (
                        <Row>
                          <Box basis={firstColumnWidth}>
                            <StacktraceCaptureModeInput
                              value={formValue.stacktraceCaptureMode}
                              edited={false}
                              onChange={formSetter('stacktraceCaptureMode')}
                              isReadOnly={isReadOnly}
                            />
                          </Box>
                        </Row>
                      )}
                      <Row>
                        <Box basis={firstColumnWidth}>
                          <SamplingBaselineInput
                            value={formValue.samplingBaseline}
                            onChange={formSetter('samplingBaseline')}
                            getFormFieldProps={getFormFieldProps}
                            isReadOnly={isReadOnly}
                          />
                        </Box>
                        <Box basis={secondColumnWidth}>
                          <SamplingFrequencyInput
                            value={formValue.samplingFrequency}
                            onChange={formSetter('samplingFrequency')}
                            getFormFieldProps={getFormFieldProps}
                            isReadOnly={isReadOnly}
                          />
                        </Box>
                        <Box basis={thirdColumnWidth}>
                          <SamplingWindowInput
                            value={formValue.samplingWindow}
                            onChange={formSetter('samplingWindow')}
                            getFormFieldProps={getFormFieldProps}
                            isReadOnly={isReadOnly}
                          />
                        </Box>
                      </Row>
                    </>
                  )}
                  {isAllowedSyslog && (
                    <Row>
                      <Box>
                        <SyslogToggleInput
                          disabled={isSyslogToggleDisabled}
                          onChange={formSetter('syslogEnabled')}
                          value={formValue.syslogEnabled}
                          edited={false}
                          disableSyslogToggle={isSyslogToggleDisabled}
                        />
                      </Box>
                    </Row>
                  )}
                  {showSyslog && (
                    <SyslogSettingsForm
                      state={formValue.syslog}
                      onChange={syslogOnChange}
                      getFormFieldProps={getFormFieldProps}
                      viewOnly={isReadOnly}
                    />
                  )}
                </Form>
                <DialogFooter>
                  {isReadOnly ? (
                    <DoneButton onClick={onCancel} />
                  ) : (
                    <>
                      <CancelButton onClick={onCancel} />
                      <SaveButton
                        disabled={isSaveButtonDisabled({
                          hasUnsavedChanges,
                          formIsValid
                        })}
                        onClick={onConfirm}
                      />
                    </>
                  )}
                </DialogFooter>
              </Dialog>
            )
          }}
        </FormValidator>
      )}
    </FormattedMessage>
  )
}
